"use client";

import { logout } from "@/libs/auth/redux/auth.slice";
import { authApi } from "@/libs/auth/redux/auth.api";
import { calendarApi } from "@/libs/calendar/redux/calendar.api";
import { checkoutApi } from "@/libs/checkout/redux/checkout.api";
import { customersApi } from "@/libs/customers/redux/customers.api";
import { dashboardApi } from "@/libs/dashboard/redux/dashboard.api";
import { groupsApi } from "@/libs/groups/redux/groups.api";
import { migrationApi } from "@/libs/migration/redux/migration.api";
import { notificationsApi } from "@/libs/notifications/redux/notifications.api";
import { nutritionPlansApi } from "@/libs/nutrition-plans/redux/nutrition-plans.api";
import { productsApi } from "@/libs/products/redux/products.api";
import { settingsApi } from "@/libs/settings/redux/settings.api";
import { surveysApi } from "@/libs/surveys/redux/surveys.api";
import { tagsApi } from "@/libs/tags/redux/tags.api";
import { trainingPlansApi } from "@/libs/training-plans/redux/trainingPlans.api";
import { vodsApi } from "@/libs/vods/redux/vod.api";
import { flushReduxPersist } from "@/libs/shared/reduxPersistFlush";
import type { AppDispatch } from "@/libs/shared/store";

const COMETCHAT_TEARDOWN_MS = 2500;

/** Avoid importing `Chat.tsx` here (pulls CometChat SDK and breaks SSR). */
function removeCometChatWidgetFromDom() {
  if (typeof document === "undefined") return;
  const el = document.getElementById("cometchat__widget");
  el?.remove();
}

/**
 * CometChat expects `logout()` before tearing down the DOM; removing `#cometchat__widget`
 * first can leave the SDK in a logged-in state and the UI can reappear or never hide.
 */
async function teardownCometChatWidget() {
  if (typeof window === "undefined") return;
  const timeout = new Promise<void>((resolve) => {
    setTimeout(resolve, COMETCHAT_TEARDOWN_MS);
  });
  const logoutWork = (async () => {
    try {
      const widget = window.CometChatWidget as
        | { logout?: () => void | Promise<void> }
        | undefined;
      await Promise.resolve(widget?.logout?.());
    } catch {
      // Widget may be absent or not ready
    }
    removeCometChatWidgetFromDom();
  })();
  await Promise.race([logoutWork, timeout]);
  removeCometChatWidgetFromDom();
}

function shutdownIntercomOnStaging() {
  if (typeof window === "undefined") return;
  if (!window.location.href.includes("-staging")) return;
  void import("@intercom/messenger-js-sdk").then(({ shutdown }) => {
    try {
      shutdown();
    } catch {
      // Intercom may not be initialized
    }
  });
}

/**
 * Clears persisted auth, all RTK Query caches, and third-party session hooks (CometChat, Intercom on staging).
 * Navigation to `/` should be handled by the private layout when `user` becomes null.
 */
export async function clearClientSession(dispatch: AppDispatch) {
  await teardownCometChatWidget();
  shutdownIntercomOnStaging();

  dispatch(logout());
  dispatch(authApi.util.resetApiState());
  dispatch(calendarApi.util.resetApiState());
  dispatch(checkoutApi.util.resetApiState());
  dispatch(customersApi.util.resetApiState());
  dispatch(dashboardApi.util.resetApiState());
  dispatch(groupsApi.util.resetApiState());
  dispatch(migrationApi.util.resetApiState());
  dispatch(notificationsApi.util.resetApiState());
  dispatch(nutritionPlansApi.util.resetApiState());
  dispatch(productsApi.util.resetApiState());
  dispatch(settingsApi.util.resetApiState());
  dispatch(surveysApi.util.resetApiState());
  dispatch(tagsApi.util.resetApiState());
  dispatch(trainingPlansApi.util.resetApiState());
  dispatch(vodsApi.util.resetApiState());

  flushReduxPersist();
}
